/**
 * Batch-3 pieces (fieldiq-api#1152): the New-report dialog, the edit dialog
 * for a draft's header fields, the report register used by the Reports page
 * and the Job page, and the refusal line.
 *
 * A FINAL report is drafted by the API when the job completes, so the dialog
 * only offers Final on a complete job — and when the job already has a live
 * final (`final_exists`), it links to that report rather than leaving the
 * reader with a refusal and no way forward.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateNdtReport, useNdtReports, useUpdateNdtReport } from '../../hooks/useNdtInspection';
import { canWriteNdtReports, ndtReportKindLabel, ndtReportNumberText, ndtReportPeriod, ndtReportRefusal, ndtReportStatusSkin, type NdtReportRefusal } from '../../lib/ndt-reports';
import type { NdtJob, NdtReport, NdtReportKind } from '../../types/ndt-inspection';
import { useOpenRecord } from '../downhole/recordPage';
import { NDT_DATE_INPUT, NDT_INPUT, NDT_PRIMARY, NDT_SECONDARY, NdtBadge, NdtDialog, NdtEmpty, NdtField, NdtFormError, NdtPanel, NdtRegister, type NdtColumn } from './NdtBits';

/** A refusal from a report write, with the link `final_exists` names. */
export function NdtReportRefusalNotice({ refusal }: { refusal: NdtReportRefusal | null }) {
  if (!refusal) return null;
  return (
    <div role="alert" data-testid="ndt-report-refusal" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      <p className="font-semibold">{refusal.message}</p>
      {refusal.code === 'final_exists' && refusal.reportId && (
        <Link to={`/ndt-inspection/reports/${refusal.reportId}`} className="mt-1 inline-block font-bold underline">
          Open the final report
        </Link>
      )}
    </div>
  );
}

export function ndtReportColumns(opts: { showJob: boolean }): NdtColumn<NdtReport>[] {
  return [
    {
      key: 'number',
      header: 'Report',
      render: (r) =>
        r.reportNumber ? <span className="font-mono font-extrabold text-white">{r.reportNumber}</span> : <span className="text-fieldiq-muted">{ndtReportNumberText(r)}</span>,
    },
    { key: 'kind', header: 'Kind', render: (r) => ndtReportKindLabel(r) },
    ...(opts.showJob
      ? [
          { key: 'job', header: 'Job', render: (r: NdtReport) => <span className="font-mono">{r.jobNumber || '—'}</span> },
          { key: 'customer', header: 'Customer', render: (r: NdtReport) => r.customerName || '—' },
        ]
      : []),
    { key: 'period', header: 'Covers', render: (r) => ndtReportPeriod(r) },
    { key: 'welds', header: 'Welds', align: 'right', render: (r) => (typeof r.stats?.weldsCovered === 'number' ? r.stats.weldsCovered : '—') },
    { key: 'exams', header: 'Exams', align: 'right', render: (r) => (typeof r.stats?.examsCovered === 'number' ? r.stats.examsCovered : '—') },
    { key: 'status', header: 'Status', render: (r) => <NdtBadge skin={ndtReportStatusSkin(r.status)} value={r.status} /> },
  ];
}

export function NdtReportCard({ r, showJob }: { r: NdtReport; showJob: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-mono text-sm font-extrabold text-white">{ndtReportNumberText(r)}</p>
        <p className="mt-0.5 text-xs text-fieldiq-muted">
          {ndtReportKindLabel(r)}
          {showJob ? ` · ${r.jobNumber || '—'} · ${r.customerName || '—'}` : ''} · {ndtReportPeriod(r)}
        </p>
      </div>
      <NdtBadge skin={ndtReportStatusSkin(r.status)} value={r.status} />
    </div>
  );
}

/** The Reports section on a job's page: its reports, a New report, and the way to the final. */
export function NdtJobReportsPanel({ job, today, role }: { job: NdtJob; today: string | null; role: string | null | undefined }) {
  const openRecord = useOpenRecord();
  const query = useNdtReports({ jobId: job.id });
  const reports = query.data?.data ?? [];
  const [adding, setAdding] = useState(false);
  const final = reports.find((r) => r.kind === 'final' && r.status !== 'void') ?? null;
  const canAdd = canWriteNdtReports(role) && job.status !== 'cancelled';
  return (
    <NdtPanel
      eyebrow="Reports"
      title="Inspection reports"
      testId="ndt-job-reports"
      actions={
        <div className="flex flex-wrap gap-2">
          {final && (
            <Link to={`/ndt-inspection/reports/${final.id}`} className={NDT_SECONDARY} data-testid="ndt-final-report-link">
              <FileText className="size-4" aria-hidden="true" />
              Final report
            </Link>
          )}
          {canAdd && (
            <button type="button" className={NDT_SECONDARY} onClick={() => setAdding(true)}>
              <Plus className="size-4" aria-hidden="true" />
              New report
            </button>
          )}
        </div>
      }
    >
      <NdtRegister
        query={query}
        rows={reports}
        columns={ndtReportColumns({ showJob: false })}
        rail={(r) => ndtReportStatusSkin(r.status).rail}
        onOpen={(r) => openRecord(`/ndt-inspection/reports/${r.id}`)}
        subject="this job's reports"
        testId="ndt-job-report"
        minWidth="min-w-[760px]"
        empty={
          <NdtEmpty>
            {job.status === 'complete'
              ? 'No report is on record for this job.'
              : 'No report yet. The final report is drafted when the job is completed; an interim or daily report can be made at any time.'}
          </NdtEmpty>
        }
        card={(r) => <NdtReportCard r={r} showJob={false} />}
      />
      {canAdd && (
        <NdtNewReportDialog
          open={adding}
          onClose={() => setAdding(false)}
          job={job}
          today={today}
          existingFinal={final}
          onCreated={(id) => openRecord(`/ndt-inspection/reports/${id}`)}
        />
      )}
    </NdtPanel>
  );
}

const footer = (formId: string, pending: boolean, label: string, onClose: () => void) => (
  <div className="flex justify-end gap-2">
    <button type="button" className={NDT_SECONDARY} onClick={onClose}>
      Close
    </button>
    <button type="submit" form={formId} className={NDT_PRIMARY} disabled={pending}>
      {pending ? 'Saving…' : label}
    </button>
  </div>
);

/** New report on one job: final (complete job only), interim (a range) or daily (one day). */
export function NdtNewReportDialog({
  open,
  onClose,
  job,
  today,
  existingFinal,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  job: Pick<NdtJob, 'id' | 'jobNumber' | 'status'>;
  today: string | null;
  existingFinal: Pick<NdtReport, 'id'> | null;
  onCreated: (id: string) => void;
}) {
  const create = useCreateNdtReport();
  const finalOffered = job.status === 'complete' && !existingFinal;
  const [kind, setKind] = useState<NdtReportKind>('daily');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<NdtReportRefusal | null>(null);
  useEffect(() => {
    if (open) {
      setKind(finalOffered ? 'final' : 'daily');
      setFrom(today ?? '');
      setTo(today ?? '');
      setTitle('');
      setError(null);
      setRefusal(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setRefusal(null);
    if (kind !== 'final' && !from) return setError(kind === 'daily' ? 'Choose the day.' : 'Choose the first day.');
    if (kind === 'interim' && !to) return setError('Choose the last day.');
    if (kind === 'interim' && to < from) return setError('The last day cannot be before the first.');
    setError(null);
    create.mutate(
      {
        job_id: job.id,
        kind,
        ...(kind === 'daily' ? { period_from: from } : kind === 'interim' ? { period_from: from, period_to: to } : {}),
        ...(title.trim() ? { title: title.trim() } : {}),
      },
      {
        onSuccess: (res) => {
          toast.success('Report drafted');
          onClose();
          if (res.data?.id) onCreated(res.data.id);
        },
        onError: (err) => setRefusal(ndtReportRefusal(err, 'The report could not be created.')),
      },
    );
  };

  return (
    <NdtDialog open={open} title={`New report — ${job.jobNumber}`} onClose={onClose} footer={footer('ndt-new-report', create.isPending, 'Create draft', onClose)}>
      <form id="ndt-new-report" onSubmit={submit} className="space-y-3" noValidate>
        <NdtFormError message={error} />
        <NdtReportRefusalNotice refusal={refusal} />
        <NdtField label="Kind">
          <select aria-label="Kind" className={NDT_INPUT} value={kind} onChange={(e) => setKind(e.target.value as NdtReportKind)}>
            {finalOffered && <option value="final">Final — the whole job</option>}
            <option value="interim">Interim — a date range</option>
            <option value="daily">Daily — one day</option>
          </select>
        </NdtField>
        {!finalOffered && (
          <p className="text-xs text-fieldiq-muted" data-testid="ndt-final-hint">
            {existingFinal ? (
              <>
                This job already has a final report.{' '}
                <Link to={`/ndt-inspection/reports/${existingFinal.id}`} className="font-bold underline">
                  Open it
                </Link>
              </>
            ) : (
              'A final report is drafted when the job is completed.'
            )}
          </p>
        )}
        {kind === 'daily' && (
          <NdtField label="Day">
            <input type="date" aria-label="Day" className={NDT_DATE_INPUT} value={from} onChange={(e) => setFrom(e.target.value)} />
          </NdtField>
        )}
        {kind === 'interim' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <NdtField label="From">
              <input type="date" aria-label="From" className={NDT_DATE_INPUT} value={from} onChange={(e) => setFrom(e.target.value)} />
            </NdtField>
            <NdtField label="To">
              <input type="date" aria-label="To" className={NDT_DATE_INPUT} value={to} onChange={(e) => setTo(e.target.value)} />
            </NdtField>
          </div>
        )}
        <NdtField label="Title (optional)">
          <input aria-label="Title" className={NDT_INPUT} value={title} onChange={(e) => setTitle(e.target.value)} />
        </NdtField>
        <p className="text-xs text-fieldiq-muted">The report lists every interpreted exam it covers. An AI summary is drafted for you to review.</p>
      </form>
    </NdtDialog>
  );
}

/** Edit a DRAFT's title, contact and references (and its period, for interim / daily). */
export function NdtEditReportDialog({ open, onClose, report }: { open: boolean; onClose: () => void; report: NdtReport }) {
  const update = useUpdateNdtReport();
  const [v, setV] = useState({ title: '', contactName: '', contactEmail: '', extRef: '', from: '', to: '' });
  const [refusal, setRefusal] = useState<NdtReportRefusal | null>(null);
  useEffect(() => {
    if (open) {
      setV({
        title: report.title ?? '',
        contactName: report.customerContactName ?? '',
        contactEmail: report.customerContactEmail ?? '',
        extRef: report.externalRef ?? '',
        from: report.periodFrom ?? '',
        to: report.periodTo ?? '',
      });
      setRefusal(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => setV((s) => ({ ...s, [k]: e.target.value }));
  const nullable = (t: string) => (t.trim() ? t.trim() : null);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setRefusal(null);
    update.mutate(
      {
        id: report.id,
        body: {
          title: nullable(v.title),
          customer_contact_name: nullable(v.contactName),
          customer_contact_email: nullable(v.contactEmail),
          external_ref: nullable(v.extRef),
          ...(report.kind === 'daily' ? { period_from: v.from } : report.kind === 'interim' ? { period_from: v.from, period_to: v.to } : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success('Report saved');
          onClose();
        },
        onError: (err) => setRefusal(ndtReportRefusal(err, 'The report could not be saved.')),
      },
    );
  };
  return (
    <NdtDialog open={open} title="Edit report" onClose={onClose} footer={footer('ndt-edit-report', update.isPending, 'Save', onClose)}>
      <form id="ndt-edit-report" onSubmit={submit} className="space-y-3" noValidate>
        <NdtReportRefusalNotice refusal={refusal} />
        <NdtField label="Title">
          <input aria-label="Title" className={NDT_INPUT} value={v.title} onChange={set('title')} />
        </NdtField>
        <div className="grid gap-3 sm:grid-cols-2">
          <NdtField label="Customer contact">
            <input aria-label="Customer contact" className={NDT_INPUT} value={v.contactName} onChange={set('contactName')} />
          </NdtField>
          <NdtField label="Contact email">
            <input aria-label="Contact email" type="email" className={NDT_INPUT} value={v.contactEmail} onChange={set('contactEmail')} />
          </NdtField>
        </div>
        <NdtField label="Customer reference">
          <input aria-label="Customer reference" className={NDT_INPUT} value={v.extRef} onChange={set('extRef')} />
        </NdtField>
        {report.kind !== 'final' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <NdtField label={report.kind === 'daily' ? 'Day' : 'From'}>
              <input type="date" aria-label={report.kind === 'daily' ? 'Day' : 'From'} className={NDT_DATE_INPUT} value={v.from} onChange={set('from')} />
            </NdtField>
            {report.kind === 'interim' && (
              <NdtField label="To">
                <input type="date" aria-label="To" className={NDT_DATE_INPUT} value={v.to} onChange={set('to')} />
              </NdtField>
            )}
          </div>
        )}
      </form>
    </NdtDialog>
  );
}
